/**
 * Композабл для управления вариантами продукта.
 * Single Responsibility — только логика вариантов.
 */

import { ref, computed, watch, type Ref } from 'vue';
import { productVariantLabel } from '../lib/productSizeLabel';
import type { Product, ProductVariant } from './types/product.types';
import { isProductGroup, isComboProduct } from './types/product.types';

function emptyVariant(): ProductVariant {
  return { id: 0, name: '', price: 0 };
}

function comboAsSyntheticVariant(combo: import('./types/product.types').ComboProduct): ProductVariant {
  return {
    id: combo.id,
    name: combo.name,
    price: combo.final_price,
    description: combo.description,
    img: combo.img,
    stop_list: combo.stock <= 0,
  };
}

export interface UseProductVariantsOptions {
  product: Ref<Product | null>;
  preferredVariantId?: Ref<number | null | undefined>;
}

export function useProductVariants(options: UseProductVariantsOptions) {
  const { product, preferredVariantId } = options;

  const selectedVariantIndex = ref(0);

  const computedVariantIndex = computed(() => {
    if (!isProductGroup(product.value)) return 0;
    const variants = product.value.variants;
    const preferredId = preferredVariantId?.value;
    if (preferredId !== null && preferredId !== undefined) {
      const index = variants.findIndex(v => String(v.id) === String(preferredId));
      return index === -1 ? 0 : index;
    }
    return 0;
  });

  const currentVariant = computed((): ProductVariant => {
    const p = product.value;
    if (!p) return emptyVariant();
    if (isProductGroup(p)) return p.variants[selectedVariantIndex.value] ?? emptyVariant();
    if (isComboProduct(p)) return comboAsSyntheticVariant(p);
    return p as ProductVariant;
  });

  const variantTabs = computed(() => {
    if (!isProductGroup(product.value)) return [];
    return product.value.variants.map(variant => ({
      label: productVariantLabel(variant),
      icon: '',
    }));
  });

  const hasVariants = computed(() => isProductGroup(product.value));
  const useRadioButtons = computed(
    () => isProductGroup(product.value) && product.value.is_radio_button,
  );

  watch(
    () => preferredVariantId?.value,
    () => { selectedVariantIndex.value = computedVariantIndex.value; },
    { immediate: true },
  );

  watch(
    product,
    () => { selectedVariantIndex.value = computedVariantIndex.value; },
    { immediate: true },
  );

  return {
    selectedVariantIndex,
    currentVariant,
    variantTabs,
    hasVariants,
    useRadioButtons,
  };
}
